import { authService } from '@common/api/authService';
import { billingService, type Plan } from '@common/api/billingService';
import {
    getCurrentProductCode,
    isPluginProduct,
    withPluginProductHeaders,
} from '@common/api/productContext';
import { resolveBillingPresentationState } from './billingPresentationState';

interface RefreshProfileFromServerOptions {
    workspaceId?: string | null;
    includeSetupCardStatus?: boolean;
}

interface WorkspaceSubscriptionLike {
    plan_id?: string | null;
    status?: string | null;
    is_in_trial?: boolean | null;
    current_period_end?: string | null;
    trial_end_at?: string | null;
    cancel_at_period_end?: boolean | null;
    plan_code?: string | null;
    plan_name?: string | null;
    billing_cycle?: string | null;
    plan_badge?: string | null;
}

export const shouldApplyBillingProfileRefresh = (
    currentProfile: Awaited<ReturnType<typeof authService.verifyAuth>> | null,
    nextProfile: Awaited<ReturnType<typeof authService.verifyAuth>> | null,
) => {
    if (!nextProfile) return false;
    if (!currentProfile) return true;

    return JSON.stringify(currentProfile.workspacePlan || null) !== JSON.stringify(nextProfile.workspacePlan || null);
};

const isFutureDate = (value?: string | null, now: Date = new Date()) => {
    if (!value) return false;
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) && timestamp > now.getTime();
};

const isTrialStatus = (status?: string | null) => {
    const normalized = (status || '').toLowerCase();
    return normalized === 'trial' || normalized === 'trialing';
};

const isActiveSubscriptionStatus = (status?: string | null) => {
    const normalized = (status || '').toLowerCase();
    return normalized === 'active' || normalized === 'paid';
};

const isNonTrialSubscriptionStatus = (status?: string | null) => {
    const normalized = (status || '').toLowerCase();
    return normalized === 'pending_cancellation'
        || normalized === 'canceled'
        || normalized === 'expired';
};

const setupCardSubscriptionIndicatesTrial = (
    subscription?: {
        status?: string | null;
        isInTrial?: boolean | null;
        trialEndAt?: string | null;
    } | null,
) => {
    if (!subscription || isNonTrialSubscriptionStatus(subscription.status)) {
        return false;
    }

    if (subscription.isInTrial !== undefined) {
        return Boolean(subscription.isInTrial && isFutureDate(subscription.trialEndAt));
    }

    return Boolean(
        subscription.isInTrial === true
        || isTrialStatus(subscription.status)
        || (subscription.isInTrial !== false && isFutureDate(subscription.trialEndAt))
    );
};

export const workspaceSubscriptionIndicatesTrial = (
    subscription?: WorkspaceSubscriptionLike | null,
    now: Date = new Date(),
) => {
    if (!subscription) return false;

    const status = (subscription.status || '').toLowerCase();
    const badge = (subscription.plan_badge || '').toLowerCase();

    if (subscription.is_in_trial !== undefined) {
        return Boolean(subscription.is_in_trial && isFutureDate(subscription.trial_end_at, now));
    }

    if (isNonTrialSubscriptionStatus(status)) {
        return false;
    }

    if (isActiveSubscriptionStatus(status)) {
        return false;
    }

    return Boolean(
        isFutureDate(subscription.trial_end_at, now)
        && (
            isTrialStatus(status)
            || badge === 'trial'
        )
    );
};

export const applyWorkspaceSubscriptionToProfile = (
    profile: Awaited<ReturnType<typeof authService.verifyAuth>> | null,
    subscription?: WorkspaceSubscriptionLike | null,
    now: Date = new Date(),
) => {
    if (!profile || !subscription) {
        return profile;
    }

    const basePlan = profile.workspacePlan;
    const isTrial = workspaceSubscriptionIndicatesTrial(subscription, now);
    const trialEndAt = subscription?.trial_end_at || basePlan?.trialEndAt || '';
    const currentPeriodEnd = subscription?.current_period_end || basePlan?.currentPeriodEnd || '';
    const planCode = (subscription?.plan_code || basePlan?.planCode || 'basic').toUpperCase();
    const status = isTrial ? 'trial' : (subscription.status || basePlan?.status || 'active');

    return {
        ...profile,
        workspacePlan: {
            ...basePlan,
            planId: subscription?.plan_id || basePlan?.planId || '',
            planCode,
            planName: subscription?.plan_name || basePlan?.planName || 'Basic',
            tier: basePlan?.tier || planCode.toLowerCase(),
            status,
            startAt: basePlan?.startAt || '',
            trialEndAt: isTrial ? trialEndAt : '',
            currentPeriodStart: basePlan?.currentPeriodStart || '',
            currentPeriodEnd: isTrial ? (trialEndAt || currentPeriodEnd) : currentPeriodEnd,
            cancelAtPeriodEnd: Boolean(subscription?.cancel_at_period_end),
            currency: basePlan?.currency || 'usd',
            billingCycle: subscription?.billing_cycle || basePlan?.billingCycle || 'monthly',
            provider: basePlan?.provider || 'stripe',
            badge: isTrial ? 'Trial' : '',
            actionLabel: isTrial ? 'Start subscription' : 'Manage',
        },
    };
};

export const buildWorkspaceSubscriptionFromSetupCardStatus = (
    setupCardStatus: Awaited<ReturnType<typeof billingService.getSetupCardStatus>> | null,
    plans: Pick<Plan, 'id' | 'code' | 'name' | 'tier' | 'billingCycle'>[] = [],
): WorkspaceSubscriptionLike | null => {
    const subscription = setupCardStatus?.subscription;
    if (!subscription?.planId) return null;

    const matchedPlan = plans.find(plan => plan.id === subscription.planId);
    const planCode = subscription.planCode || matchedPlan?.code || null;
    const planName = subscription.planName || matchedPlan?.name || null;

    if (!planCode && !planName) {
        return null;
    }

    return {
        plan_id: subscription.planId,
        plan_code: planCode,
        plan_name: planName,
        billing_cycle: subscription.billingCycle || matchedPlan?.billingCycle || null,
        status: setupCardSubscriptionIndicatesTrial(subscription) ? 'trial' : subscription.status,
        trial_end_at: subscription.trialEndAt || null,
        is_in_trial: subscription.isInTrial,
        current_period_end: subscription.currentPeriodEnd || null,
        cancel_at_period_end: Boolean(subscription.cancelAtPeriodEnd),
        plan_badge: setupCardSubscriptionIndicatesTrial(subscription) ? 'Trial' : '',
    };
};

export const applyWorkspaceSubscriptionTrialToProfile = (
    profile: Awaited<ReturnType<typeof authService.verifyAuth>> | null,
    subscription?: WorkspaceSubscriptionLike | null,
    now: Date = new Date(),
) => {
    if (!workspaceSubscriptionIndicatesTrial(subscription, now)) {
        return profile;
    }

    return applyWorkspaceSubscriptionToProfile(profile, subscription, now);
};

export const applySetupCardTrialStatusToProfile = (
    profile: Awaited<ReturnType<typeof authService.verifyAuth>> | null,
    setupCardStatus: Awaited<ReturnType<typeof billingService.getSetupCardStatus>> | null,
    isCurrentSetupCardTrial = setupCardSubscriptionIndicatesTrial(setupCardStatus?.subscription),
) => {
    if (!profile?.workspacePlan) return profile;

    const subscription = setupCardStatus?.subscription;
    const isTrial = isCurrentSetupCardTrial;

    if (!isTrial) {
        return profile;
    }

    const trialEndAt = subscription?.trialEndAt || profile.workspacePlan.trialEndAt;

    return {
        ...profile,
        workspacePlan: {
            ...profile.workspacePlan,
            status: 'trial',
            trialEndAt,
            currentPeriodEnd: trialEndAt || subscription?.currentPeriodEnd || profile.workspacePlan.currentPeriodEnd,
            badge: 'Trial',
            actionLabel: 'Start subscription',
        },
    };
};

export const applySetupCardStatusToProfile = (
    profile: Awaited<ReturnType<typeof authService.verifyAuth>> | null,
    setupCardStatus: Awaited<ReturnType<typeof billingService.getSetupCardStatus>> | null,
) => {
    if (!profile) return profile;

    const subscription = setupCardStatus?.subscription;
    const subscriptionStatus = (subscription?.status || '').toLowerCase();
    const billingPresentationState = resolveBillingPresentationState({
        // This Core-only endpoint predates an explicit provider field. Preserve its
        // existing Stripe behavior when neither response nor profile supplies one.
        provider: subscription?.provider || profile.workspacePlan?.provider || 'stripe',
        subscriptionStatus,
        requiresPaymentMethodUpdate: setupCardStatus?.requiresPaymentMethodUpdate,
    });
    const hasExplicitStripeProvider = subscription?.provider?.toLowerCase() === 'stripe';
    const hasKnownStripeNonTrialState = hasExplicitStripeProvider && (
        billingPresentationState.kind === 'paid'
        || billingPresentationState.kind === 'payment_pending'
        || billingPresentationState.kind === 'payment_failed'
    );
    const isCurrentSetupCardTrial = setupCardSubscriptionIndicatesTrial(subscription)
        && (!hasKnownStripeNonTrialState || subscription?.isInTrial === true);
    const trialProfile = applySetupCardTrialStatusToProfile(
        profile,
        setupCardStatus,
        isCurrentSetupCardTrial,
    );
    if (trialProfile !== profile) {
        return trialProfile;
    }

    const hasStripeSubscriptionState = Boolean(
        subscription
        && !isCurrentSetupCardTrial
        && (
            billingPresentationState.kind === 'paid'
            || billingPresentationState.kind === 'payment_pending'
            || billingPresentationState.kind === 'payment_failed'
        ),
    );

    if (!hasStripeSubscriptionState || !profile.workspacePlan) {
        return profile;
    }

    return {
        ...profile,
        workspacePlan: {
            ...profile.workspacePlan,
            status: subscription?.status || 'active',
            provider: subscription?.provider || profile.workspacePlan.provider || 'stripe',
            trialEndAt: '',
            currentPeriodEnd: subscription?.currentPeriodEnd || profile.workspacePlan.currentPeriodEnd,
            badge: '',
            actionLabel: 'Manage',
            primaryAction: 'Manage',
            statusLabel: profile.workspacePlan.planName,
            ...(setupCardStatus?.requiresPaymentMethodUpdate !== undefined
                ? { requiresPaymentMethodUpdate: setupCardStatus.requiresPaymentMethodUpdate }
                : {}),
        },
    };
};

export const applyBillingPrefetchProfileState = (
    profile: Awaited<ReturnType<typeof authService.verifyAuth>> | null,
    setupCardStatus: Awaited<ReturnType<typeof billingService.getSetupCardStatus>> | null,
    workspaceSubscription?: WorkspaceSubscriptionLike | null,
    now: Date = new Date(),
) => {
    const profileWithWorkspaceSubscription = applyWorkspaceSubscriptionToProfile(
        profile,
        workspaceSubscription,
        now,
    );
    const profileWithSetupCardStatus = applySetupCardStatusToProfile(
        profileWithWorkspaceSubscription,
        setupCardStatus,
    );

    if (setupCardStatus?.subscription && profileWithSetupCardStatus?.workspacePlan) {
        return profileWithSetupCardStatus;
    }

    return profileWithWorkspaceSubscription;
};

export const refreshProfileFromServer = async ({
    workspaceId,
    includeSetupCardStatus = false,
}: RefreshProfileFromServerOptions = {}) => {
    let setupCardStatus: Awaited<ReturnType<typeof billingService.getSetupCardStatus>> | null = null;
    const workspaceRequestConfig = workspaceId
        ? { headers: { 'X-Workspace-Id': workspaceId } }
        : undefined;
    const isPluginProfileRefresh = isPluginProduct(getCurrentProductCode());
    const productScopedWorkspaceRequestConfig = isPluginProfileRefresh
        ? withPluginProductHeaders(workspaceRequestConfig)
        : workspaceRequestConfig;

    if (includeSetupCardStatus && workspaceId && !isPluginProfileRefresh) {
        setupCardStatus = await billingService.getSetupCardStatus(productScopedWorkspaceRequestConfig);
    }

    const profile = await authService.verifyAuth(productScopedWorkspaceRequestConfig);
    return applySetupCardStatusToProfile(profile, setupCardStatus);
};
